import { VALIDATION_STATUS } from "./types";

/**
 * @param {import('./types').ValidationTestResult} raw
 */
function normalizeResult(raw) {
  if (!raw || typeof raw !== "object") {
    return {
      status: VALIDATION_STATUS.ERROR,
      messages: [{ level: VALIDATION_STATUS.ERROR, message: "Test returned invalid result" }],
    };
  }

  const messages = Array.isArray(raw.messages) ? raw.messages : [];
  let status = raw.status || VALIDATION_STATUS.SUCCESS;

  if (![VALIDATION_STATUS.SUCCESS, VALIDATION_STATUS.WARNING, VALIDATION_STATUS.ERROR, VALIDATION_STATUS.SKIP].includes(status)) {
    status = VALIDATION_STATUS.ERROR;
  }

  if (status !== VALIDATION_STATUS.SKIP) {
    const hasError = messages.some((m) => m.level === VALIDATION_STATUS.ERROR);
    const hasWarning = messages.some((m) => m.level === VALIDATION_STATUS.WARNING);
    if (hasError) status = VALIDATION_STATUS.ERROR;
    else if (hasWarning) status = VALIDATION_STATUS.WARNING;
    else if (messages.length === 0) status = VALIDATION_STATUS.SUCCESS;
  }

  return {
    status,
    messages,
    developerNotes: raw.developerNotes,
  };
}

/**
 * Run all validation tests in parallel.
 * @param {import('./types').ValidationTestDefinition[]} tests
 * @param {import('./types').ValidationContext} context
 * @returns {Promise<import('./types').ValidationRunResult[]>}
 */
export async function runValidators(tests, context) {
  const runners = tests.map(async (test) => {
    try {
      const raw = await test.run(context);
      const normalized = normalizeResult(raw);
      return { test, ...normalized };
    } catch (err) {
      return {
        test,
        status: VALIDATION_STATUS.ERROR,
        messages: [
          {
            level: VALIDATION_STATUS.ERROR,
            message: err?.message || "Test threw an unexpected error",
          },
        ],
        developerNotes: err?.stack || String(err),
      };
    }
  });

  return Promise.all(runners);
}
